/**
 * Adapter for converting between Workflow JSON task format and unified format
 */

import { BaseAdapter, UnifiedTask } from './base-adapter.js';
import type { Task, TaskStatus, TaskPriority } from '../types/index.js';

export class WorkflowAdapter extends BaseAdapter<Task, UnifiedTask> {
  protected readonly sourceFormat = 'workflow-json';
  protected readonly targetFormat = 'unified';
  
  /**
   * Convert Workflow task to unified format
   */
  toUnified(workflowTask: Task): UnifiedTask {
    if (!this.validateSource(workflowTask)) {
      this.handleError(new Error('Invalid Workflow task'), 'toUnified');
    }
    
    const now = new Date();
    
    return {
      // Core fields
      id: workflowTask.id,
      title: workflowTask.content,
      description: workflowTask.section,
      status: this.mapStatus(workflowTask.status),
      priority: this.mapPriority(workflowTask.priority),
      
      // Extended fields
      assignee: workflowTask.assignee,
      tags: workflowTask.tags || [],
      estimate: undefined, // Workflow doesn't have estimate field
      dependencies: [], // Workflow doesn't have dependencies field
      
      // Workflow-specific fields preserved
      complexity: undefined, // Will be added when complexity analyzer is integrated
      workflowId: undefined,
      phase: undefined,
      
      // Metadata
      metadata: {
        source: 'workflow',
        format: 'json',
        version: '1.0',
        created: workflowTask.createdAt ? new Date(workflowTask.createdAt) : now,
        updated: workflowTask.updatedAt ? new Date(workflowTask.updatedAt) : now,
      },
      
      // Timestamps
      created: workflowTask.createdAt ? new Date(workflowTask.createdAt) : now,
      updated: workflowTask.updatedAt ? new Date(workflowTask.updatedAt) : now,
      completed: workflowTask.completedAt ? new Date(workflowTask.completedAt) : undefined,
    };
  }
  
  /**
   * Convert unified task back to Workflow format
   */
  fromUnified(unified: UnifiedTask): Task {
    if (!this.validateUnified(unified)) {
      this.handleError(new Error('Invalid unified task'), 'fromUnified');
    }
    
    const workflowTask: Task = {
      id: unified.id,
      content: unified.title,
      status: this.reverseMapStatus(unified.status),
      priority: this.reverseMapPriority(unified.priority),
      position: 0, // Will be set based on existing tasks
      section: unified.description,
      tags: unified.tags || [],
      assignee: unified.assignee,
      createdAt: unified.created instanceof Date ? unified.created.toISOString() : unified.created,
      updatedAt: unified.updated instanceof Date ? unified.updated.toISOString() : unified.updated,
    };
    
    // Add optional fields if present
    if (unified.completed) {
      workflowTask.completedAt = unified.completed instanceof Date ? unified.completed.toISOString() : unified.completed;
    }
    
    return workflowTask;
  }
  
  /**
   * Map Workflow status to unified status
   */
  private mapStatus(workflowStatus: TaskStatus): UnifiedTask['status'] {
    const statusMap: Record<TaskStatus, UnifiedTask['status']> = {
      'pending': 'todo',
      'in_progress': 'in_progress',
      'completed': 'done',
      'cancelled': 'todo', // Map cancelled to todo with a tag
    };
    
    return statusMap[workflowStatus] || 'todo';
  }
  
  /**
   * Map unified status back to Workflow status
   */
  private reverseMapStatus(unifiedStatus: UnifiedTask['status']): TaskStatus {
    const statusMap: Record<UnifiedTask['status'], TaskStatus> = {
      'todo': 'pending',
      'in_progress': 'in_progress',
      'review': 'in_progress', // Workflow doesn't have review status
      'done': 'completed',
    };
    
    return statusMap[unifiedStatus];
  }
  
  /**
   * Map Workflow priority to unified priority
   */
  private mapPriority(workflowPriority: TaskPriority): UnifiedTask['priority'] {
    const priorityMap: Record<TaskPriority, UnifiedTask['priority']> = {
      'high': 'high',
      'medium': 'normal',
      'low': 'low',
    };
    
    return priorityMap[workflowPriority] || 'normal';
  }
  
  /**
   * Map unified priority back to Workflow priority
   */
  private reverseMapPriority(unifiedPriority: UnifiedTask['priority']): TaskPriority {
    const priorityMap: Record<UnifiedTask['priority'], TaskPriority> = {
      'urgent': 'high', // Workflow doesn't have urgent
      'high': 'high',
      'normal': 'medium',
      'low': 'low',
    };
    
    return priorityMap[unifiedPriority];
  }
}